<template>
  <el-dialog
    :title="$t('recharge.payCreateShipment')"
    :visible.sync="dialogVisible"
    :close-on-click-modal="false"
    width="600px"
    append-to-body
    @opened="mountCurrent"
    @closed="cleanup"
  >
    <div class="order-pay">
      <div class="pay-amount">${{ amountText }}</div>
      <el-radio-group v-model="method" size="small" class="method-row" @change="changeMethod">
        <el-radio-button label="balance">{{ $t('recharge.methodBalance') }}</el-radio-button>
        <el-radio-button label="card">{{ $t('recharge.methodCard') }}</el-radio-button>
        <el-radio-button label="paypal">{{ $t('recharge.methodPaypal') }}</el-radio-button>
        <el-radio-button label="wechatpay">{{ $t('recharge.methodWechat') }}</el-radio-button>
        <el-radio-button label="alipay">{{ $t('recharge.methodAlipay') }}</el-radio-button>
      </el-radio-group>

      <div class="pay-content" v-loading="loading" :element-loading-text="$t('recharge.preparingPayment')">
        <div v-if="method === 'balance'" class="balance-pay">
          <div class="balance-info">{{ $t('recharge.balanceAvailable') }}：${{ balanceText }}</div>
          <el-button type="primary" class="pay-btn" :loading="paying" @click="useBalance">
            {{ $t('recharge.payCreateShipment') }}
          </el-button>
        </div>
        <div v-else-if="method === 'card'">
          <div :id="stripeId" class="stripe-el"></div>
          <el-button v-show="!loading" type="primary" class="pay-btn" :loading="paying" @click="stripePay">
            {{ paying ? $t('recharge.paying') : $t('recharge.payNow') }}
          </el-button>
        </div>
        <div v-else-if="method === 'paypal'" :id="paypalId"></div>
        <div v-else class="qr-wrap">
          <div class="qr-tip">{{ method === 'alipay' ? $t('recharge.scanAlipayTip') : $t('recharge.scanWechatTip') }}</div>
          <div :id="qrId" class="qr-box"></div>
          <div class="qr-countdown">{{ countdownText }}</div>
          <el-button v-if="qrExpired" size="small" @click="mountHantepay">{{ $t('recharge.regenerate') }}</el-button>
        </div>
        <div v-if="message" class="pay-message">{{ message }}</div>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import {
  rechargeStripeCreate,
  rechargeStripeConfirm,
  rechargePaypalConfig,
  rechargePaypalCreate,
  rechargePaypalCapture,
  rechargeHantepayCreate,
  rechargeHantepayQuery,
} from '@/api/recharge';

function loadScript(src, globalName) {
  return new Promise((resolve, reject) => {
    if (globalName && window[globalName]) return resolve(window[globalName]);
    const exist = document.querySelector('script[data-src="' + src + '"]');
    if (exist) {
      if (exist.getAttribute('data-loaded') === '1') return resolve(globalName ? window[globalName] : true);
      exist.addEventListener('load', () => resolve(globalName ? window[globalName] : true));
      exist.addEventListener('error', reject);
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.setAttribute('data-src', src);
    script.onload = () => {
      script.setAttribute('data-loaded', '1');
      resolve(globalName ? window[globalName] : true);
    };
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

const EXPIRE = 300;
const INTERVAL = 3000;

export default {
  name: 'OrderPayDialog',
  props: {
    visible: { type: Boolean, default: false },
    amount: { type: [Number, String], default: 0 },
  },
  data() {
    const uid = Math.random().toString(36).slice(2);
    return {
      method: 'balance',
      loading: false,
      paying: false,
      message: '',
      rechargeSn: '',
      hantepaySns: [],
      pollIndex: 0,
      pollBusy: false,
      creatingQr: false,
      paymentVersion: 0,
      paymentDone: false,
      stripe: null,
      elements: null,
      qrExpired: false,
      remaining: 0,
      pollTimer: null,
      countdownTimer: null,
      stripeId: 'order-pay-stripe-' + uid,
      paypalId: 'order-pay-paypal-' + uid,
      qrId: 'order-pay-qr-' + uid,
    };
  },
  computed: {
    dialogVisible: {
      get() { return this.visible; },
      set(value) { this.$emit('update:visible', value); },
    },
    amountText() {
      return Number(this.amount || 0).toFixed(2);
    },
    balanceText() {
      return Number(this.$store.state.money || 0).toFixed(2);
    },
    countdownText() {
      if (this.qrExpired) return this.$t('recharge.qrExpired');
      if (this.remaining <= 0) return '';
      const m = Math.floor(this.remaining / 60);
      const s = this.remaining % 60;
      return this.$t('recharge.qrLeft') + ' ' + (m < 10 ? '0' : '') + m + ':' + (s < 10 ? '0' : '') + s;
    },
  },
  watch: {
    visible(value) {
      if (value) {
        this.method = 'balance';
      } else {
        this.cleanupPayment();
      }
    },
  },
  methods: {
    changeMethod() {
      this.cleanupPayment();
      this.$nextTick(this.mountCurrent);
    },
    mountCurrent() {
      if (!this.visible) return;
      if (this.method === 'balance') this.loading = false;
      else if (this.method === 'card') this.mountStripe();
      else if (this.method === 'paypal') this.mountPaypal();
      else this.mountHantepay();
    },
    useBalance() {
      if (this.paying) return;
      this.paying = true;
      this.paySuccess();
    },
    paySuccess(money) {
      if (!this.visible || this.paymentDone) return;
      this.paymentDone = true;
      this.stopTimers();
      if (money !== undefined && money !== null && money !== '') this.$store.dispatch('updateMoney', money);
      this.dialogVisible = false;
      this.$emit('success', money);
    },
    async mountStripe() {
      this.loading = true;
      this.message = '';
      try {
        const res = await rechargeStripeCreate(Number(this.amount));
        if (!res || res.code != 1 || !res.data || !res.data.client_secret) throw new Error((res && res.msg) || this.$t('recharge.netErr'));
        this.rechargeSn = res.data.recharge_sn;
        const StripeCtor = await loadScript('https://js.stripe.com/v3/', 'Stripe');
        this.stripe = StripeCtor(res.data.publishable_key);
        this.elements = this.stripe.elements({ clientSecret: res.data.client_secret, locale: window.localStorage.getItem('lhkdLang') === 'en' ? 'en' : 'zh' });
        const element = this.elements.create('payment');
        element.on('ready', () => { this.loading = false; });
        element.mount('#' + this.stripeId);
      } catch (e) {
        this.loading = false;
        this.message = e.message || this.$t('recharge.netErr');
      }
    },
    async stripePay() {
      if (this.paying || !this.stripe || !this.elements) return;
      this.paying = true;
      this.message = '';
      try {
        const result = await this.stripe.confirmPayment({ elements: this.elements, redirect: 'if_required' });
        if (result.error) throw new Error(result.error.message || this.$t('recharge.payFail'));
        const response = await rechargeStripeConfirm(this.rechargeSn);
        if (!response || response.code != 1 || !response.data) throw new Error((response && response.msg) || this.$t('recharge.payFail'));
        this.paySuccess(response.data.money);
      } catch (e) {
        this.paying = false;
        this.message = e.message || this.$t('recharge.netErr');
      }
    },
    async mountPaypal() {
      this.loading = true;
      this.message = '';
      try {
        const cfg = await rechargePaypalConfig();
        if (!cfg || cfg.code != 1 || !cfg.data || !cfg.data.client_id) throw new Error((cfg && cfg.msg) || this.$t('recharge.netErr'));
        const url = 'https://www.paypal.com/sdk/js?client-id=' + encodeURIComponent(cfg.data.client_id)
          + '&currency=' + encodeURIComponent(cfg.data.currency || 'USD') + '&intent=capture&components=buttons';
        const paypal = await loadScript(url, 'paypal');
        this.loading = false;
        const self = this;
        paypal.Buttons({
          style: { layout: 'vertical', height: 44, shape: 'rect', label: 'paypal' },
          createOrder() {
            return rechargePaypalCreate(Number(self.amount)).then((res) => {
              if (res && res.code == 1 && res.data && res.data.order_id) {
                self.rechargeSn = res.data.recharge_sn;
                return res.data.order_id;
              }
              throw new Error((res && res.msg) || self.$t('recharge.payFail'));
            });
          },
          onApprove(data) {
            return rechargePaypalCapture(self.rechargeSn, data.orderID).then((res) => {
              if (res && res.code == 1 && res.data) self.paySuccess(res.data.money);
              else throw new Error((res && res.msg) || self.$t('recharge.payFail'));
            });
          },
          onError(error) { self.message = (error && error.message) || self.$t('recharge.payFail'); },
        }).render('#' + this.paypalId);
      } catch (e) {
        this.loading = false;
        this.message = e.message || this.$t('recharge.netErr');
      }
    },
    async mountHantepay() {
      if (this.creatingQr || !this.visible || this.paymentDone) return;
      const version = this.paymentVersion;
      this.creatingQr = true;
      this.stopTimers();
      this.loading = true;
      this.message = '';
      try {
        if (this.rechargeSn) {
          await this.checkHantepay(this.rechargeSn, version);
          if (version !== this.paymentVersion || !this.visible || this.paymentDone) return;
        }
        const res = await rechargeHantepayCreate(Number(this.amount), this.method);
        if (version !== this.paymentVersion || !this.visible || this.paymentDone) return;
        if (!res || res.code != 1 || !res.data || !res.data.code_url) throw new Error((res && res.msg) || this.$t('recharge.netErr'));
        this.rechargeSn = res.data.recharge_sn;
        this.hantepaySns.push(this.rechargeSn);
        const QRCode = await loadScript('https://www.ecpai.com/js/qrcode.min.js', 'QRCode');
        if (version !== this.paymentVersion || !this.visible || this.paymentDone) return;
        const target = document.getElementById(this.qrId);
        if (target) {
          target.innerHTML = '';
          new QRCode(target, { text: res.data.code_url, width: 200, height: 200, correctLevel: QRCode.CorrectLevel.M });
        }
        this.qrExpired = false;
        this.startCountdown();
      } catch (e) {
        if (version === this.paymentVersion && this.visible && !this.paymentDone) this.message = e.message || this.$t('recharge.netErr');
      } finally {
        if (version === this.paymentVersion) {
          this.creatingQr = false;
          this.loading = false;
          if (this.visible && !this.paymentDone && this.hantepaySns.length) this.startPolling();
        }
      }
    },
    startCountdown() {
      this.remaining = EXPIRE;
      this.countdownTimer = setInterval(() => {
        this.remaining--;
        if (this.remaining <= 0) {
          // 倒计时只控制二维码提示，支付确认继续，避免漏掉延迟付款。
          clearInterval(this.countdownTimer);
          this.countdownTimer = null;
          this.qrExpired = true;
        }
      }, 1000);
    },
    async checkHantepay(sn, version) {
      const res = await rechargeHantepayQuery(sn);
      if (version !== this.paymentVersion || !this.visible || this.paymentDone) return;
      if (!res || res.code != 1 || !res.data) throw new Error((res && res.msg) || this.$t('recharge.netErr'));
      if (res.data.status === 'paid') this.paySuccess(res.data.money);
    },
    startPolling() {
      if (this.pollTimer) clearInterval(this.pollTimer);
      this.pollTimer = setInterval(async () => {
        if (this.pollBusy || !this.visible || this.paymentDone || !this.hantepaySns.length) return;
        const version = this.paymentVersion;
        const sn = this.hantepaySns[this.pollIndex++ % this.hantepaySns.length];
        this.pollBusy = true;
        try {
          await this.checkHantepay(sn, version);
        } catch (e) {
          if (version === this.paymentVersion && this.visible && !this.paymentDone) this.message = e.message || this.$t('recharge.netErr');
        } finally {
          if (version === this.paymentVersion) this.pollBusy = false;
        }
      }, INTERVAL);
    },
    stopTimers() {
      if (this.pollTimer) clearInterval(this.pollTimer);
      if (this.countdownTimer) clearInterval(this.countdownTimer);
      this.pollTimer = null;
      this.countdownTimer = null;
    },
    cleanupPayment() {
      this.paymentVersion++;
      this.hantepaySns = [];
      this.rechargeSn = '';
      this.pollIndex = 0;
      this.pollBusy = false;
      this.creatingQr = false;
      this.paymentDone = false;
      this.stopTimers();
      this.loading = false;
      this.paying = false;
      this.message = '';
      this.stripe = null;
      this.elements = null;
      [this.stripeId, this.paypalId, this.qrId].forEach((id) => {
        const target = document.getElementById(id);
        if (target) target.innerHTML = '';
      });
    },
    cleanup() {
      this.cleanupPayment();
      this.$emit('update:visible', false);
    },
  },
  beforeDestroy() {
    this.paymentVersion++;
    this.stopTimers();
  },
};
</script>

<style lang="scss" scoped>
.pay-amount { text-align: center; font-size: 28px; font-weight: 700; color: #3563d6; }
.method-row { display: flex; flex-wrap: wrap; justify-content: center; margin: 18px 0; }
.pay-content { min-height: 130px; }
.balance-pay { text-align: center; }
.balance-info { margin: 18px 0; color: #606266; }
.stripe-el { min-height: 42px; }
.pay-btn { width: 100%; margin-top: 14px; }
.qr-wrap { text-align: center; }
.qr-tip { margin-bottom: 12px; color: #606266; font-size: 13px; }
.qr-box { display: inline-block; min-height: 200px; }
.qr-countdown { min-height: 18px; margin-top: 8px; color: #909399; font-size: 12px; }
.pay-message { margin-top: 12px; color: #f56c6c; text-align: center; word-break: break-all; }
</style>
